/**
 * ResultReport — Page container for Resultatrapport (Income Statement).
 *
 * Responsibilities:
 * - Layout (PageTitle, controls bar, card wrapper)
 * - Period/year selector
 * - Toggle switches (verifications, compact mode)
 * - PDF export button
 * - Verification details modal
 * - Delegates data to useResultReport hook and rendering to IncomeStatementTable
 */

import React, { useState } from 'react';
import { Card, Button, Alert, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/free-solid-svg-icons';
import { useParams, useHistory, Link } from 'react-router-dom';
import Select from 'react-select';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageTitle from '../../../layouts/PageTitle';
import { NoData } from '../../SharedComponents/NoData';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';

import useResultReport from './useResultReport';
import IncomeStatementTable from './IncomeStatementTable';
import { exportResultReportToPDF } from './resultReportUtils';
import './ResultReport.css';

const ResultReport = () => {
    const { companyId } = useParams();
    const history = useHistory();

    // Data hook
    const {
        fiscalYears,
        selectedFiscalYear,
        selectedPeriod,
        yearPeriodOptions,
        loading,
        error,
        reportData,
        processedData,
        subtotals,
        metadata,
        showVerifications,
        handleYearPeriodChange,
        setShowVerifications,
        setError,
        refreshReport,
    } = useResultReport(companyId);

    // Local UI state
    const [compactMode, setCompactMode] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [selectedVerification, setSelectedVerification] = useState(null);

    // -----------------------------------------------------------------------
    // Verification click handler
    // -----------------------------------------------------------------------

    const handleVerificationClick = async (verification) => {
        if (!verification?.verification_id) return;
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/verifications/${verification.verification_id}`),
                { withCredentials: true }
            );
            setSelectedVerification(response.data);
            setShowVerificationModal(true);
        } catch (err) {
            setSelectedVerification({
                verification_id: verification.verification_id,
                series: verification.verification_number?.charAt(0) || 'A',
                number: verification.verification_number?.slice(1) || verification.verification_number,
                date: verification.date,
                description: verification.description,
                transactions: [],
            });
            setShowVerificationModal(true);
        }
    };

    // -----------------------------------------------------------------------
    // Loading / Error / Empty states
    // -----------------------------------------------------------------------

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status" />
                <p className="mt-3">Laddar resultatrapport...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                <Alert.Heading>Fel vid hämtning av data</Alert.Heading>
                <p>{error}</p>
            </Alert>
        );
    }

    if (fiscalYears.length === 0) {
        return (
            <>
                <PageTitle motherMenu="Rapporter" activeMenu="Resultatrapport" />
                <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
                <div className="text-center mt-4">
                    <Link to="#" onClick={() => history.push(`/${companyId}/settings/bookkeeping#rakenskapsar`)} className="btn btn-primary">
                        Gå till Inställningar
                    </Link>
                </div>
            </>
        );
    }

    if (!reportData || Object.keys(reportData).length === 0) {
        return (
            <>
                <PageTitle motherMenu="Rapporter" activeMenu="Resultatrapport" />
                <Alert variant="info">Ingen data tillgänglig för det valda räkenskapsåret.</Alert>
            </>
        );
    }

    if (!processedData) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status" />
                <p className="mt-3">Bearbetar data...</p>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Period selector value lookup
    // -----------------------------------------------------------------------

    const currentSelectValue = (() => {
        const fyId = selectedFiscalYear?.fiscal_year_id;
        const p = selectedPeriod?.value;
        if (!fyId || !p) return null;
        for (const group of yearPeriodOptions) {
            for (const o of group.options) {
                const v = o.value;
                if (
                    v.fiscalYear?.fiscal_year_id === fyId &&
                    v.period?.type === p.type &&
                    v.period?.startDate === p.startDate &&
                    v.period?.endDate === p.endDate
                ) {
                    return o;
                }
            }
        }
        return null;
    })();

    // -----------------------------------------------------------------------
    // Metadata warnings
    // -----------------------------------------------------------------------

    const currentYearMeta = metadata[processedData.years[0]];
    const hasUnclassified = currentYearMeta?.has_unclassified;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <div className="result-report">
            <PageTitle motherMenu="Rapporter" activeMenu="Resultatrapport" />

            {/* Controls bar */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <span>År/Period:</span>
                    <Select
                        value={currentSelectValue}
                        onChange={handleYearPeriodChange}
                        options={yearPeriodOptions}
                        styles={{
                            container: (base) => ({ ...base, minWidth: '300px' }),
                            menuList: (base) => ({ ...base, maxHeight: 280 }),
                        }}
                    />

                    <Form.Check
                        type="switch"
                        id="verifications-switch"
                        label="Visa verifikationer"
                        checked={showVerifications}
                        onChange={() => setShowVerifications(!showVerifications)}
                        style={{ marginLeft: '1rem' }}
                    />

                    <Form.Check
                        type="switch"
                        id="compact-switch"
                        label="Kompakt"
                        checked={compactMode}
                        onChange={() => setCompactMode(!compactMode)}
                        style={{ marginLeft: '0.5rem' }}
                    />
                </div>

                <Button
                    variant="primary"
                    onClick={() => exportResultReportToPDF(processedData, subtotals)}
                    disabled={!processedData}
                >
                    <FontAwesomeIcon icon={faFileDownload} className="me-2" />
                    Exportera PDF
                </Button>
            </div>

            {/* Warnings */}
            {hasUnclassified && (
                <Alert variant="warning" className="mb-3">
                    <strong>Varning:</strong> Vissa konton kunde inte klassificeras i resultaträkningen.
                    Kontrollera sektionen "Oklassificerade konton" nedan.
                </Alert>
            )}

            {/* Metadata badge */}
            {currentYearMeta && (
                <div className="mb-2" style={{ fontSize: '0.8em', color: '#6c757d' }}>
                    Räkenskapsår: {currentYearMeta.fiscal_year_state === 'closed' ? 'Stängt' : 'Öppet'}
                    {currentYearMeta.verification_count !== undefined && (
                        <> · {currentYearMeta.verification_count} verifikationer</>
                    )}
                </div>
            )}

            {/* Income Statement Card */}
            <Card>
                <Card.Header>
                    <h5>Resultaträkning</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <IncomeStatementTable
                        processedData={processedData}
                        subtotals={subtotals}
                        showVerifications={showVerifications}
                        compactMode={compactMode}
                        onVerificationClick={handleVerificationClick}
                    />
                </Card.Body>
            </Card>

            {/* Verification Details Modal */}
            <VerificationDetailsModal
                showVerificationDetails={showVerificationModal}
                setShowVerificationDetails={setShowVerificationModal}
                selectedRow={selectedVerification}
                onVerificationUpdated={refreshReport}
                viewOnly={true}
            />

        </div>
    );
};

export default ResultReport;
